// Franchise Hub Types
// Types for /api/v2/franchise/:id/snapshot

export enum PageType {
  EVENT = 'event',
  CAMPAIGN = 'campaign'
}

export enum FranchiseEditionType {
  BY_YEAR = 'byYear',
  NAMED = 'named'
}

export interface FranchiseEditionLinkedEntityModel {
  id: number;
  name: string;
  year: number | null;
  linkedEntityType: PageType;
  currency: string;
  allowsTicketing: boolean;
  
  // Donation metrics
  donationCount: number | null;
  donationOnlineCount: number | null;
  donationFeeCoveredCount: number | null;
  donationFeeCollected: number | null;
  donationRaisedAmount: number | null;
  donationRaisedOnlineAmount: number | null;
  donationAverageAmount: number | null;
  donationAverageOnlineAmount: number | null;
  
  // Sale metrics
  saleCount: number | null;
  saleOnlineCount: number | null;
  saleFeeCoveredCount: number | null;
  saleFeeCollected: number | null;
  saleRaisedAmount: number | null;
  saleRaisedOnlineAmount: number | null;
  saleDetailCount: number | null;
  
  // Fee metrics
  feeCoveragePercentage: number | null;
  feeAmountCoveragePercentage: number | null;
  feeCollected: number | null;
  
  // Overall metrics
  raisedAmount: number | null;
  
  // Pledge metrics
  pledgeCount: number | null;
  pledgeRaisedAmount: number | null;
  
  // Fundraiser metrics
  fundraiserAverageRaisedAmount: number | null;
  fundraiserRaisedAmount: number | null;
  
  // Page metrics
  campaignCount: number | null;
  campaignLiveCount: number | null;
  campaignWithDonationsCount: number | null;
  fundraiserCount: number | null;
  fundraiserLiveCount: number | null;
  fundraiserWithDonationsCount: number | null;
  teamCount: number | null;
  teamLiveCount: number | null;
  teamWithDonationsCount: number | null;
  collectionCount: number | null;
  collectionLiveCount: number | null;
  collectionWithDonationsCount: number | null;
  participantCount: number | null;
  
  // Ambassador/PlaceHub metrics (Events only)
  ambassadorTotalCount: number | null;
  ambassadorPageCount: number | null;
  ambassadorFundraisedAmount: number | null;
  ambassadorFundraisedOnlineAmount: number | null;
}

export interface FranchiseEditionSnapshotModel {
  id: number;
  name: string;
  year: number | null;
  linkedEntities: FranchiseEditionLinkedEntityModel[];
}

export interface FranchiseSnapshotModel {
  id: number;
  name: string;
  editionType: FranchiseEditionType;
  editions: FranchiseEditionSnapshotModel[];
}

export interface FranchiseSnapshotResponse {
  data: FranchiseSnapshotModel;
  success: boolean;
  error: string | null;
}

// UI-specific types

export interface AggregatedMetrics {
  // Counts
  donationCount: number;
  donationOnlineCount: number;
  saleCount: number;
  saleOnlineCount: number;
  pledgeCount: number;
  saleDetailCount: number;
  
  // Amounts
  donationRaisedAmount: number;
  donationRaisedOnlineAmount: number;
  saleRaisedAmount: number;
  saleRaisedOnlineAmount: number;
  pledgeRaisedAmount: number;
  fundraiserRaisedAmount: number;
  raisedAmount: number;
  
  // Averages (calculated, not summed)
  donationAverageAmount: number;
  donationAverageOnlineAmount: number;
  fundraiserAverageRaisedAmount: number;
  
  // Fees
  feeCollected: number;
  donationFeeCollected: number;
  saleFeeCollected: number;
  
  // Fee percentages (averaged)
  feeCoveragePercentage: number;
  feeAmountCoveragePercentage: number;
  
  // Calculated percentages
  feeCollectedPercentage: number; // feeCollected / raisedAmount
  
  // Page counts (summed)
  fundraiserCount: number;
  fundraiserWithDonationsCount: number;
  teamCount: number;
  teamWithDonationsCount: number;
  collectionCount: number;
  collectionWithDonationsCount: number;
  pageCount: number; // fundraiserCount + teamCount + collectionCount
  pagesWithDonations: number; // fundraiserWithDonationsCount + teamWithDonationsCount + collectionWithDonationsCount
  
  // Ambassador/PlaceHub metrics (summed)
  ambassadorTotalCount: number;
  ambassadorPageCount: number;
  ambassadorFundraisedAmount: number;
  ambassadorFundraisedOnlineAmount: number;
}

export interface EntityWithComparison extends FranchiseEditionLinkedEntityModel {
  // Year-over-year comparison data
  previousYearEntity?: FranchiseEditionLinkedEntityModel;
  yoyChanges?: {
    // Support YoY for all 12 selectable metrics
    raisedAmount?: number;
    donationCount?: number;
    feeCollected?: number;
    donationAverageAmount?: number;
    saleRaisedAmount?: number;
    donationRaisedAmount?: number;
    saleDetailCount?: number;
    pageCount?: number;
    pagesWithDonations?: number;
    fundraiserAverageRaisedAmount?: number;
    ambassadorTotalCount?: number;
    ambassadorPageCount?: number;
    ambassadorFundraisedAmount?: number;
  };
}

export interface ProcessedEdition {
  edition: FranchiseEditionSnapshotModel;
  entities: EntityWithComparison[];
  displayYear: number | null; // The year used for grouping (from entity or edition)
  totalRaised: number; // Aggregated total raised for this edition
}

export interface GroupedByYear {
  [year: number]: EntityWithComparison[];
}

export type ViewMode = 'named' | 'byYear';

// Metric selection types
export type MetricKey = 
  // Row 1 - Always visible
  | 'raisedAmount' 
  | 'donationCount' 
  | 'feeCollected' 
  | 'donationAverageAmount'
  // Row 2 - Sales (show if sale data exists)
  | 'saleRaisedAmount'
  | 'donationRaisedAmount'
  | 'saleDetailCount'
  // Row 3 - Fundraising (show if any fundraising pages exist)
  | 'pageCount' // calculated: fundraiserCount + teamCount + collectionCount
  | 'pagesWithDonations' // calculated: fundraiserWithDonationsCount + teamWithDonationsCount + collectionWithDonationsCount
  | 'fundraiserAverageRaisedAmount'
  // Row 4 - PlaceHub (show if ambassadorTotalCount > 0)
  | 'ambassadorTotalCount'
  | 'ambassadorPageCount'
  | 'ambassadorFundraisedAmount';

